import { PhotoProgress } from 'src/onboarding/components/ui/photo-progress';
import '../../pages/lts-photo-example/style.scss';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import { t } from '@web-solutions/module-localization';
import exclamation from '../../assets/images/exclamation.png';
import Button from 'src/onboarding/components/ui/button/Button';
import { useEffect, useMemo, useRef, useState } from 'react';
import ScanLoader from '../ScanLoader';
import { motion } from 'framer-motion';
import HorizontalLoader from '../HorizontalLoader';
import { useNav } from 'src/onboarding/hooks/nav';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
interface Landmark {
  type: string;
  x: number;
  y: number;
}
interface BoundingBox {
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
}
interface FaceData {
  boundingBox: BoundingBox;
  landmarks: Landmark[];
}

const FaceScanOverlay = ({
  imageUrl,
  base64Image,
  isBlurred,
  hideScanLine,
  isImageLoaded,
  showErrorPopUp,
  errorTitle,
  errorDescription,
  isBodyScan,
  onImageLoad,
  onChoosePhoto,
  onSkip,
  faceData,
  showLandmarks = false,
}: {
  imageUrl?: string;
  base64Image?: string;
  isBlurred?: boolean;
  hideScanLine: boolean;
  isImageLoaded: boolean;
  showErrorPopUp: boolean;
  errorTitle: string;
  errorDescription: string;
  isBodyScan?: boolean;
  faceData?: FaceData;
  showLandmarks?: boolean;
  onImageLoad: () => void;
  onChoosePhoto: () => void;
  onSkip: () => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { onNext } = useNav();
  const { darkBlurEnabled } = useRemoteConfig();

  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [showScanLoader, setShowScanLoader] = useState(false);
  const [shouldZoomImage, setShouldZoomImage] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [landmarkAnimationProgress, setLandmarkAnimationProgress] = useState(0);
  const [showContainerCircle, setShowContainerCircle] = useState(false);
  const [allPointsPhaseComplete, setAllPointsPhaseComplete] = useState(false); // Флаг завершения показа всех точек

  const EXCLUDED_LANDMARK_TYPES = useMemo(
    () => [
      // 'RIGHT_EYE_LEFT_CORNER',
      // 'RIGHT_EYE_RIGHT_CORNER',
      // 'LEFT_EYE_LEFT_CORNER',
      // 'LEFT_EYE_RIGHT_CORNER',
      // 'LEFT_OF_LEFT_EYEBROW',
      // 'RIGHT_OF_LEFT_EYEBROW',
      // 'LEFT_OF_RIGHT_EYEBROW',
      // 'RIGHT_OF_RIGHT_EYEBROW',
      // 'FOREHEAD_GLABELLA',
      // 'NOSE_BOTTOM_CENTER',
    ],
    []
  );

  const [zoomScale, setZoomScale] = useState(3);

  // ИЛИ самая простая версия с минимальным увеличением:
  const calculateZoomScaleMinimal = (pointType: string) => {
    if (!faceData || !imageDimensions.width) return 1.5;

    const { boundingBox } = faceData;

    // Вычисляем размер лица в пикселях
    const faceWidthPx = boundingBox.xMax - boundingBox.xMin;
    const faceHeightPx = boundingBox.yMax - boundingBox.yMin;

    // ПРЕОБРАЗОВАНИЕ: если значения > 1, вероятно это пиксели
    // Нормализуем относительно размеров изображения
    let faceWidthNormalized = faceWidthPx;
    let faceHeightNormalized = faceHeightPx;

    // Если значения больше 1, вероятно это пиксели
    if (faceWidthPx > 1 || faceHeightPx > 1) {
      // Делим на размер изображения для нормализации
      faceWidthNormalized = faceWidthPx / imageDimensions.width;
      faceHeightNormalized = faceHeightPx / imageDimensions.height;
    }

    const faceSize = Math.max(faceWidthNormalized, faceHeightNormalized);

    // Базовые масштабы
    const baseScales = {
      RIGHT_EYE: 3,
      RIGHT_CHEEK_CENTER: 3,
      RIGHT_EYEBROW_UPPER_MIDPOINT: 3,
    };

    const baseScale = baseScales[pointType as keyof typeof baseScales] || 1.5;

    // Корректировка на основе нормализованного размера
    if (faceSize < 0.2) {
      return baseScale * 2;
    } else if (faceSize < 0.3) {
      return baseScale * 1.8;
    } else if (faceSize < 0.5) {
      return baseScale * 1.3;
    } else {
      return baseScale;
    }
  };

  // формируем последовательность точек
  const scanSequence = useMemo(() => {
    if (!faceData) return [];

    return [
      faceData.landmarks.find((l) => l.type === 'RIGHT_EYE'),
      faceData.landmarks.find((l) => l.type === 'RIGHT_CHEEK_CENTER'),
      faceData.landmarks.find((l) => l.type === 'RIGHT_EYEBROW_UPPER_MIDPOINT'),
    ].filter(Boolean) as Landmark[];
  }, [faceData]);

  const scanLoaderLabels = useMemo(
    () => [
      t('onboarding.faceScanPopup.eye'),
      t('onboarding.faceScanPopup.skin'),
      t('onboarding.faceScanPopup.eyeBrow'),
    ],
    []
  );

  // активная точка
  const currentPoint = scanSequence[currentIndex];

  const currentScanLoaderLabel = scanLoaderLabels[currentIndex] || '';

  useEffect(() => {
    if (showLandmarks && faceData) {
      let fadeInInterval: NodeJS.Timeout;
      const fadeInDuration = 1500;
      const fadeInSteps = 30;
      let currentStep = 0;

      fadeInInterval = setInterval(() => {
        currentStep++;
        const progress = currentStep / fadeInSteps;
        setLandmarkAnimationProgress(progress);

        if (currentStep >= fadeInSteps) {
          clearInterval(fadeInInterval);

          setTimeout(() => {
            const fadeOutDuration = 1500;
            const fadeOutSteps = 30;
            let fadeOutStep = 0;

            const fadeOutInterval = setInterval(() => {
              fadeOutStep++;
              const progress = 1 - fadeOutStep / fadeOutSteps;
              setLandmarkAnimationProgress(progress);

              if (fadeOutStep >= fadeOutSteps) {
                clearInterval(fadeOutInterval);
                setAllPointsPhaseComplete(true);

                // Рассчитываем масштаб для первой точки
                const firstPoint = faceData.landmarks.find((l) => l.type === 'RIGHT_EYE');
                if (firstPoint) {
                  // Используйте calculateZoomScaleMinimal для минимального увеличения
                  const scale = calculateZoomScaleMinimal(firstPoint.type);
                  setZoomScale(scale);
                }

                setShouldZoomImage(true);

                setTimeout(() => {
                  setShowScanLoader(true);

                  setTimeout(() => {
                    setShowScanLoader(false);
                    setCurrentIndex(1);

                    // Обновляем масштаб для второй точки
                    const secondPoint = faceData.landmarks.find(
                      (l) => l.type === 'RIGHT_CHEEK_CENTER'
                    );
                    if (secondPoint) {
                      const scale = calculateZoomScaleMinimal(secondPoint.type);
                      setZoomScale(scale);
                    }

                    setTimeout(() => {
                      setShowScanLoader(true);

                      setTimeout(() => {
                        setShowScanLoader(false);
                        setCurrentIndex(2);

                        // Обновляем масштаб для третьей точки
                        const thirdPoint = faceData.landmarks.find(
                          (l) => l.type === 'RIGHT_EYEBROW_UPPER_MIDPOINT'
                        );
                        if (thirdPoint) {
                          const scale = calculateZoomScaleMinimal(thirdPoint.type);
                          setZoomScale(scale);
                        }

                        setTimeout(() => {
                          setShowScanLoader(true);

                          setTimeout(() => {
                            setShowScanLoader(false);
                            setShowContainerCircle(true);
                            setTimeout(() => {
                              onNext();
                            }, 2000);
                          }, 4000);
                        }, 1500);
                      }, 4000);
                    }, 1500);
                  }, 4000);
                }, 1500);
              }
            }, fadeOutDuration / fadeOutSteps);
          }, 2000);
        }
      }, fadeInDuration / fadeInSteps);

      return () => {
        clearInterval(fadeInInterval);
      };
    }
  }, [showLandmarks, faceData]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageDimensions({
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
    });
    onImageLoad();
  };

  // вычисляем offset от текущей точки
  useEffect(() => {
    if (!currentPoint || !containerRef.current || imageDimensions.width === 0 || !faceData) return;

    const rect = containerRef.current.getBoundingClientRect();
    const imgWidth = imageDimensions.width;
    const imgHeight = imageDimensions.height;

    // РАСЧЕТ object-fit: cover
    const containerAspect = rect.width / rect.height;
    const imageAspect = imgWidth / imgHeight;

    let drawWidth, drawHeight, offsetX, offsetY;

    if (containerAspect > imageAspect) {
      // Контейнер шире изображения
      drawWidth = rect.width;
      drawHeight = rect.width / imageAspect;
      offsetX = 0;
      offsetY = (rect.height - drawHeight) / 2;
    } else {
      // Контейнер уже изображения
      drawHeight = rect.height;
      drawWidth = rect.height * imageAspect;
      offsetX = (rect.width - drawWidth) / 2;
      offsetY = 0;
    }

    const scaleX = drawWidth / imgWidth;
    const scaleY = drawHeight / imgHeight;

    const px = currentPoint.x * scaleX + offsetX;
    const py = currentPoint.y * scaleY + offsetY;

    const frameWidth = 126;
    const frameHeight = 126;
    const frameCenterX = rect.width - 20 - frameWidth / 2;
    const frameCenterY = rect.height / 2;

    let adjustX = -90;
    let adjustY = -10;

    if (currentPoint.type === 'RIGHT_EYEBROW_UPPER_MIDPOINT') {
      adjustX = -90;
      adjustY = -10;
    }

    setOffset({
      x: frameCenterX - px + adjustX,
      y: frameCenterY - py + adjustY,
    });
  }, [currentPoint, imageDimensions, faceData]);

  const drawLandmarks = () => {
    if (!canvasRef.current || !imageRef.current || !faceData || !showLandmarks) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const img = imageRef.current;
    const imgWidth = img.naturalWidth || img.width;
    const imgHeight = img.naturalHeight || img.height;

    if (imgWidth === 0 || imgHeight === 0 || canvas.width === 0 || canvas.height === 0) return;

    // Use logical canvas size (CSS size) for calculations, not physical size
    const logicalCanvasWidth = canvas.width / dpr;
    const logicalCanvasHeight = canvas.height / dpr;

    // РАСЧЕТ РЕАЛЬНЫХ РАЗМЕРОВ ИЗОБРАЖЕНИЯ С УЧЕТОМ object-fit: cover
    const containerAspect = logicalCanvasWidth / logicalCanvasHeight;
    const imageAspect = imgWidth / imgHeight;

    let drawWidth, drawHeight, offsetX, offsetY;

    if (containerAspect > imageAspect) {
      // Контейнер шире изображения - масштабируем по ширине
      drawWidth = logicalCanvasWidth;
      drawHeight = logicalCanvasWidth / imageAspect;
      offsetX = 0;
      offsetY = (logicalCanvasHeight - drawHeight) / 2;
    } else {
      // Контейнер уже изображения - масштабируем по высоте
      drawHeight = logicalCanvasHeight;
      drawWidth = logicalCanvasHeight * imageAspect;
      offsetX = (logicalCanvasWidth - drawWidth) / 2;
      offsetY = 0;
    }

    // Теперь scaleX и scaleY рассчитываем относительно реальной области рисования
    const scaleX = drawWidth / imgWidth;
    const scaleY = drawHeight / imgHeight;

    if (!allPointsPhaseComplete) {
      if (landmarkAnimationProgress > 0) {
        const filteredLandmarks = faceData.landmarks.filter(
          (landmark) => !EXCLUDED_LANDMARK_TYPES.includes(landmark.type)
        );

        const currentOpacity = 0.7 * landmarkAnimationProgress;
        const fixedRadius = 4;

        ctx.globalAlpha = currentOpacity;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        filteredLandmarks.forEach((landmark) => {
          const pointX = landmark.x * scaleX + offsetX;
          const pointY = landmark.y * scaleY + offsetY;

          if (pointX >= 0 && pointX <= logicalCanvasWidth && pointY >= 0 && pointY <= logicalCanvasHeight) {
            const color = 'rgba(241, 241, 241, 1)';

            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(pointX, pointY, fixedRadius, 0, Math.PI * 2);
            ctx.fill();
          }
        });

        ctx.globalAlpha = 1;
      }
    }
  };

  useEffect(() => {
    if (showLandmarks && faceData && imageDimensions.width > 0) {
      if (canvasRef.current && imageRef.current) {
        const imgRect = imageRef.current.getBoundingClientRect();
        const canvas = canvasRef.current;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = imgRect.width * dpr;
        canvas.height = imgRect.height * dpr;
        canvas.style.width = imgRect.width + 'px';
        canvas.style.height = imgRect.height + 'px';
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.scale(dpr, dpr);
        drawLandmarks();
      }
    }
  }, [
    showLandmarks,
    faceData,
    imageDimensions,
    landmarkAnimationProgress,
    allPointsPhaseComplete,
    currentPoint,
    currentIndex,
    shouldZoomImage,
  ]);

  useEffect(() => {
    const updateCanvasSize = () => {
      if (imageRef.current && canvasRef.current) {
        const imgRect = imageRef.current.getBoundingClientRect();
        const canvas = canvasRef.current;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = imgRect.width * dpr;
        canvas.height = imgRect.height * dpr;
        canvas.style.width = imgRect.width + 'px';
        canvas.style.height = imgRect.height + 'px';
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.scale(dpr, dpr);
        if (showLandmarks && faceData && imageDimensions.width > 0) {
          drawLandmarks();
        }
      }
    };
    window.addEventListener('resize', updateCanvasSize);
    setTimeout(updateCanvasSize, 100);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [showLandmarks, faceData, imageDimensions]);

  return (
    <div className='face-scan-overlay' style={{ backgroundColor: 'white' }}>
      {showContainerCircle && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.76)',
            zIndex: 20,
            pointerEvents: 'none',
          }}
        />
      )}

      <div ref={containerRef} className='image-container'>
        <motion.img
          ref={imageRef}
          onLoad={handleImageLoad}
          src={imageUrl || base64Image}
          alt='user-upload'
          className={isBlurred ? 'blurred' : ''}
          initial={{ scale: 1, x: 0, y: 0 }}
          style={{ position: 'relative', zIndex: 1 }}
          animate={
            shouldZoomImage && currentPoint
              ? {
                  scale: zoomScale,
                  x: offset.x * zoomScale,
                  y: offset.y * zoomScale,
                  transition: { duration: 1.5, ease: 'easeInOut' },
                }
              : { scale: 1, x: 0, y: 0 }
          }
        />

        {showLandmarks && (
          <>
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            />
          </>
        )}
        {showScanLoader && allPointsPhaseComplete && (
          <>
            {darkBlurEnabled ? (
              <ScanLoader label={currentScanLoaderLabel} />
            ) : (
              <div
                style={{
                  position: 'absolute',
                  right: '0px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 15,
                }}
              >
                <ScanLoader label={currentScanLoaderLabel} />
              </div>
            )}
          </>
        )}

        {showLandmarks && !showErrorPopUp && (
          <div className='dark-overlay'>
            <HorizontalLoader duration={22000} />
          </div>
        )}
        {showContainerCircle && (
          <div className='containerCircle' style={{ zIndex: 30 }}>
            <div className='cicrlceBg'></div>
            <div className='circle'></div>
            <svg className='checkmark' viewBox='0 0 52 52'>
              <path
                className='checkmarkPath'
                fill='none'
                stroke='#00CE00'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='6'
                d='M14 27 l10 10 l18 -18'
              />
            </svg>
          </div>
        )}
      </div>

      {!hideScanLine && isImageLoaded && !shouldZoomImage && <div className='scan-line' />}

      {isBlurred && !showErrorPopUp && !isBodyScan && !shouldZoomImage && (
        <>
          <PhotoProgress />
          <div className='scan-description'>
            <Title tag='h3'>{t('onboarding.lts-photo-example.faceScan.title')}</Title>
            <Text size='m' medium>
              {t('onboarding.lts-photo-example.faceScan.subTitle')}
            </Text>
          </div>
        </>
      )}

      {showErrorPopUp && (
        <>
          <div className='error-popup'>
            <div className='popup-title'>
              <img className='exclamation' src={exclamation} alt='exclamation' />

              <Text semibold>{errorTitle}</Text>
            </div>
            <Text regular size='m' className='error-text'>
              {errorDescription}
            </Text>
            <div className='popup-controls'>
              <Button
                title={t('onboarding.lts-photo-example.faceScan.choosePhoto')}
                onClick={onChoosePhoto}
              />
              <Button
                title={t('onboarding.lts-photo-example.faceScan.skip')}
                type={'secondary'}
                className='later-button'
                onClick={onSkip}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FaceScanOverlay;
